import { writeoffApi } from '../writeoffApi';
import { WriteoffDocumentView } from '../types/documentTypes';

const documentEndpoints = writeoffApi.injectEndpoints({
  endpoints: (builder) => ({
    // List documents for a writeoff
    // GET /writeoffs/{company}/{center}/{writeoffId}/documents
    listWriteoffDocuments: builder.query<
      WriteoffDocumentView[],
      { company: string; center: string; writeoffId: string }
    >({
      query: ({ company, center, writeoffId }) => ({
        url: `writeoffs/${company}/${center}/${writeoffId}/documents`,
        method: 'GET',
      }),
      providesTags: (result, error, { company, center, writeoffId }) => [
        { type: 'WriteoffDocument', id: `${company}-${center}-${writeoffId}` },
        'WriteoffDocument',
      ],
    }),

    // Upload document to a writeoff
    // POST /writeoffs/{company}/{center}/{writeoffId}/documents
    uploadWriteoffDocument: builder.mutation<
      WriteoffDocumentView,
      { company: string; center: string; writeoffId: string; file: File; documentType: string }
    >({
      query: ({ company, center, writeoffId, file, documentType }) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('documentType', documentType);

        return {
          url: `writeoffs/${company}/${center}/${writeoffId}/documents`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: (result, error, { company, center, writeoffId }) => [
        { type: 'WriteoffDocument', id: `${company}-${center}-${writeoffId}` },
        'WriteoffDocument',
      ],
    }),

    // Download document
    // GET /writeoffs/{company}/{center}/{writeoffId}/documents/{documentId}/download
    downloadWriteoffDocument: builder.mutation<
      Blob,
      { company: string; center: string; writeoffId: string; documentId: string }
    >({
      query: ({ company, center, writeoffId, documentId }) => ({
        url: `writeoffs/${company}/${center}/${writeoffId}/documents/${documentId}/download`,
        method: 'GET',
        responseHandler: (response) => response.blob(),
      }),
    }),

    // View document (get document for viewing)
    // GET /writeoffs/{company}/{center}/{writeoffId}/documents/{documentId}/view
    viewWriteoffDocument: builder.query<
      Blob,
      { company: string; center: string; writeoffId: string; documentId: string }
    >({
      query: ({ company, center, writeoffId, documentId }) => ({
        url: `writeoffs/${company}/${center}/${writeoffId}/documents/${documentId}/view`,
        method: 'GET',
        responseHandler: (response) => response.blob(),
      }),
    }),

    // Delete document
    // DELETE /writeoffs/{company}/{center}/{writeoffId}/documents/{documentId}
    deleteWriteoffDocument: builder.mutation<
      void,
      { company: string; center: string; writeoffId: string; documentId: string }
    >({
      query: ({ company, center, writeoffId, documentId }) => ({
        url: `writeoffs/${company}/${center}/${writeoffId}/documents/${documentId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { company, center, writeoffId }) => [
        { type: 'WriteoffDocument', id: `${company}-${center}-${writeoffId}` },
        'WriteoffDocument',
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useListWriteoffDocumentsQuery,
  useUploadWriteoffDocumentMutation,
  useDownloadWriteoffDocumentMutation,
  useViewWriteoffDocumentQuery,
  useDeleteWriteoffDocumentMutation,
} = documentEndpoints;
